import { createContext, useContext, useEffect } from 'react';

export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbsContextValue {
  crumbs: Crumb[];
  setCrumbs: (next: Crumb[]) => void;
}

export const BreadcrumbsContext =
  createContext<BreadcrumbsContextValue | null>(null);

export function useBreadcrumbs(): Crumb[] {
  const ctx = useContext(BreadcrumbsContext);
  if (!ctx) throw new Error('useBreadcrumbs must be used inside <BreadcrumbsProvider>');
  return ctx.crumbs;
}

/**
 * Set the breadcrumb trail for the current page. Pass an array of crumbs in
 * order, leaf last (e.g. `[{ label: 'Companies', to: '/companies' }, { label: company.name }]`).
 * The trail is cleared automatically when the component calling this hook unmounts,
 * so navigating away from the page falls back to the route-derived default.
 */
export function useSetBreadcrumbs(crumbs: Crumb[]) {
  const ctx = useContext(BreadcrumbsContext);
  if (!ctx) throw new Error('useSetBreadcrumbs must be used inside <BreadcrumbsProvider>');
  const { setCrumbs } = ctx;
  const key = crumbs.map((c) => `${c.label}|${c.to ?? ''}`).join('>');
  useEffect(() => {
    setCrumbs(crumbs);
    return () => setCrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setCrumbs]);
}
